import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Chip,
  TextField,
  Pagination,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { useNavigate } from "react-router-dom";

import Header1 from "../Header/Header1";
import Footer2 from "../Footer/Footer2";

const API_URL = process.env.REACT_APP_API_URL;

const RequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTimeFilter, setStartTimeFilter] = useState("");
  const [endTimeFilter, setEndTimeFilter] = useState("");

  const [openMediaDialog, setOpenMediaDialog] = useState(false);
  const [mediaURL, setMediaURL] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 8;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const openDetails = (id) => {
    navigate(`/request-details-page/${id}`);
  };

  // ============================
  // FETCH REQUESTS
  // ============================
 useEffect(() => {
  axios
    .get(`${API_URL}/api/requests1/`)
    .then((response) => {
      const list = Array.isArray(response.data)
        ? response.data
        : [response.data];

      // Sort by created_at (Newest First)
      const sorted = list.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA; // newest first
      });

      setRequests(sorted);
      setFilteredRequests(sorted);
      setLoading(false);
    })
    .catch((err) => {
      console.error(err);
      setError("Failed to load requests");
      setLoading(false);
    });
}, []);


  // ============================
  // FILTERING
  // ============================
  useEffect(() => {
    let filtered = [...requests];

    const text = searchText.toLowerCase();

    filtered = filtered.filter((req) => {
      return (
        req.description?.toLowerCase().includes(text) ||
        req.language?.toLowerCase().includes(text)
      );
    });

    if (startDate) {
      filtered = filtered.filter(
        (req) => new Date(req.selectedDate) >= new Date(startDate)
      );
    }

    if (endDate) {
      filtered = filtered.filter(
        (req) => new Date(req.selectedDate) <= new Date(endDate)
      );
    }

    if (startTimeFilter) {
      filtered = filtered.filter((req) => req.startTime >= startTimeFilter);
    }

    if (endTimeFilter) {
      filtered = filtered.filter((req) => req.startTime <= endTimeFilter);
    }

    setFilteredRequests(filtered);
    setPage(1);
  }, [searchText, startDate, endDate, startTimeFilter, endTimeFilter, requests]);

  // ============================
  // PAGINATION
  // ============================
  const paginatedData = filteredRequests.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const totalPages = Math.ceil(filteredRequests.length / pageSize);

  // ============================
  // MEDIA DIALOG HANDLER
  // ============================
  const openMediaViewer = (media) => {
    setMediaURL(media);
    setOpenMediaDialog(true);
  };

  const closeMediaViewer = () => {
    setOpenMediaDialog(false);
    setMediaURL("");
  };

  // ============================
  // UI
  // ============================
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" mt={4}>
        {error}
      </Typography>
    );
  }

  return (
   <div>
  <Header1 />

  <Box p={4}>
    <Typography variant="h4" mb={3} fontWeight="bold">
      All Requests
    </Typography>

    {/* Search */}
    <TextField
      label="Search by description"
      fullWidth
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      sx={{ mb: 3 }}
    />

    {/* Filters */}
    <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
      <Box flex="1 1 200px">
        <TextField
          label="Start Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </Box>

      <Box flex="1 1 200px">
        <TextField
          label="End Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </Box>

      <Box flex="1 1 200px">
        <TextField
          label="Start Time"
          type="time"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={startTimeFilter}
          onChange={(e) => setStartTimeFilter(e.target.value)}
        />
      </Box>

      <Box flex="1 1 200px">
        <TextField
          label="End Time"
          type="time"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={endTimeFilter}
          onChange={(e) => setEndTimeFilter(e.target.value)}
        />
      </Box>
    </Box>

    {/* Count */}
    <Typography variant="h6" mb={3}>
      Showing <strong>{filteredRequests.length}</strong> requests
    </Typography>

    {/* Cards */}
    <Box display="flex" flexWrap="wrap" gap={3}>
      {paginatedData.map((req) => (
        <Box
          key={req.id}
          flex="1 1 calc(25% - 16px)" // responsive: 4 per row
          minWidth="280px"
        >
          <Card
            sx={{
              height: '100%',
              cursor: "pointer",
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              transition: "0.3s",
              "&:hover": {
                transform: "translateY(-4px) scale(1.02)",
                boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
              },
            }}
          >
            <CardContent onClick={() => openDetails(req.id)} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Description Focus */}
              <Typography variant="body1" fontWeight="bold" mb={1} sx={{ color: '#cecacaff', lineHeight: 1.5 }}>
                {req.description || "No description available"}
              </Typography>

              {/* Optional language tag */}
              {req.language && (
                <Chip label={req.language} size="small" sx={{ mb: 1, alignSelf: 'flex-start', backgroundColor: '#e0e0e0', color: '#555' }} />
              )}

              {/* Price & Duration */}
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip label={`Price: $${req.price}`} size="small" />
                <Chip label={`Duration: ${req.duration} hrs`} size="small" />
              </Box>

              {/* Date & Time */}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Date:</strong> {req.selectedDate || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Start:</strong> {req.startTime || "N/A"}
                </Typography>
              </Box>

              {/* Media Button */}
              {req.media_url && (
                <Box mt="auto">
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      openMediaViewer(req.media_url);
                    }}
                  >
                    View Media
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      ))}
    </Box>

    {/* Pagination */}
    <Box mt={4} display="flex" justifyContent="center">
      <Pagination
        count={totalPages}
        page={page}
        onChange={(e, value) => setPage(value)}
        size="large"
        color="primary"
      />
    </Box>
  </Box>

  <Footer2 />

  {/* Media Viewer Dialog */}
  <Dialog open={openMediaDialog} onClose={closeMediaViewer} maxWidth="md" fullWidth>
    <DialogTitle>Media Viewer</DialogTitle>
    <DialogContent>
      <Box display="flex" justifyContent="center">
        <iframe
          src={mediaURL}
          title="media"
          width="100%"
          height="500px"
          style={{ border: 0 }}
        />
      </Box>
    </DialogContent>
  </Dialog>
</div>


  );
};

export default RequestsPage;
